import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, SlidersHorizontal } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import ImpactSummary from '../components/CustomerBilling/ImpactSummary';
import IncreaseBands from '../components/CustomerBilling/IncreaseBands';
import CustomerList from '../components/CustomerBilling/CustomerList';
import CustomerDetail from '../components/CustomerBilling/CustomerDetail';
import ScenarioEditor from '../components/CustomerBilling/ScenarioEditor';
import { periodLabel } from '../components/CustomerBilling/format';
import { c, btn, card } from '../theme';

// Customer Billing — what every customer was billed, and what a billing
// change would do to them and to the company, on one screen: the company
// impact on top, the customer list and one customer's detail below.
//
// Three selections drive everything, and they are held HERE so every panel
// answers the same question:
//   * the baseline period (the month whose bills are re-priced),
//   * the scenario (a % per charge category; none = projected equals billed),
//   * the company filter.
// The band, the category and the search narrow the LIST only. They must not rescope the tiles:
// a band click that also filtered the company totals would make "Customers
// affected" read 298 and look like the whole company.
export default function CustomerBilling() {
  const [period, setPeriod]         = useState(null);    // null = newest held
  const [scenarioId, setScenarioId] = useState('');
  const [company, setCompany]       = useState('');
  const [band, setBand]             = useState(null);
  const [category, setCategory]     = useState(null);
  const [search, setSearch]         = useState('');
  const [sort, setSort]             = useState('change_desc');
  const [page, setPage]             = useState(0);
  const [account, setAccount]       = useState(null);
  const [editing, setEditing]       = useState(null);    // null | 'new' | scenario

  const metaQ      = useQuery({ queryKey: ['billing-meta'], queryFn: () => api.get('/customer-billing/meta') });
  const periodsQ   = useQuery({ queryKey: ['billing-periods'], queryFn: () => api.get('/customer-billing/periods') });
  const scenariosQ = useQuery({ queryKey: ['billing-scenarios'], queryFn: () => api.get('/customer-billing/scenarios') });

  const periods   = periodsQ.data || [];
  const scenarios = scenariosQ.data || [];
  const meta      = metaQ.data || { categories: [], bands: [], companies: [] };
  const scenario  = scenarios.find((x) => x.id === scenarioId) || null;

  // A scenario deleted elsewhere (or by this user in the editor) must not
  // leave the board asking for an id the server now 404s on.
  useEffect(() => {
    if (scenarioId && scenariosQ.isSuccess && !scenario) setScenarioId('');
  }, [scenarioId, scenariosQ.isSuccess, scenario]);

  const ctx = useMemo(() => {
    const p = {};
    if (period) p.period = period;
    if (scenarioId) p.scenario_id = scenarioId;
    if (company) p.company = company;
    return p;
  }, [period, scenarioId, company]);

  const summaryQ = useQuery({
    queryKey: ['billing-summary', ctx],
    queryFn:  () => api.get('/customer-billing/summary', { params: ctx }),
    enabled:  periodsQ.isSuccess && periods.length > 0,
  });

  const listParams = useMemo(() => {
    const p = { ...ctx, sort, limit: PAGE, offset: page * PAGE };
    if (band) p.band = band;
    if (category) p.category = category;
    if (search) p.q = search;
    return p;
  }, [ctx, band, category, search, sort, page]);

  const customersQ = useQuery({
    queryKey: ['billing-customers', listParams],
    queryFn:  () => api.get('/customer-billing/customers', { params: listParams }),
    enabled:  periodsQ.isSuccess && periods.length > 0,
    placeholderData: (prev) => prev,
  });

  // Open on the first customer in the list, so the detail is never an empty
  // panel. Only when nothing is chosen: re-sorting or paging must not yank
  // the customer somebody is reading out from under them.
  const firstRow = customersQ.data?.rows?.[0]?.account;
  useEffect(() => {
    if (!account && firstRow) setAccount(firstRow);
  }, [account, firstRow]);

  // A search is somebody asking for a customer by number, so when it narrows
  // the list and the open customer is not in it, the first match opens.
  // Re-sorting and paging are not asks, and keep whoever is open.
  const listRows = customersQ.isPlaceholderData ? null : customersQ.data?.rows;
  useEffect(() => {
    if (!search || !listRows?.length) return;
    if (!listRows.some((r) => r.account === account)) setAccount(listRows[0].account);
  }, [search, listRows, account]);

  const detailQ = useQuery({
    queryKey: ['billing-customer', account, ctx],
    queryFn:  () => api.get(`/customer-billing/customers/${encodeURIComponent(account)}`, { params: { ...ctx, company: undefined } }),
    enabled:  !!account && periods.length > 0,
    placeholderData: (prev) => (prev?.account === account ? prev : undefined),
  });

  // Anything that changes which customers are listed starts the list at page 1.
  function narrow(fn) { return (v) => { fn(v); setPage(0); }; }

  // Choosing a category lists its customers by what they were billed for it;
  // clearing it goes back to the whole-bill order, since that sort would
  // otherwise be refused without a category to sort by.
  function chooseCategory(key) {
    setCategory(key);
    setSort(key ? 'category_desc' : (sort === 'category_desc' ? 'change_desc' : sort));
    setPage(0);
  }

  const exportParams = useMemo(() => {
    const p = { ...ctx, sort };
    if (band) p.band = band;
    if (category) p.category = category;
    if (search) p.q = search;
    return p;
  }, [ctx, band, category, search, sort]);

  if (periodsQ.isLoading) return <div style={s.page}><LoadingSpinner /></div>;

  const controls = (
    <div style={s.controls}>
      <label style={s.pickLabel}>
        <span style={s.pickCaption}>Scenario</span>
        <span style={{ display: 'flex', gap: '6px' }}>
          <select id="billing-scenario" aria-label="Scenario" value={scenarioId}
                  onChange={(e) => setScenarioId(e.target.value)} style={{ ...s.pick, minWidth: '240px' }}>
            <option value="">No change (billed as is)</option>
            {scenarios.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <button type="button" style={s.iconBtn} onClick={() => setEditing(scenario || 'new')}
                  aria-label={scenario ? `Edit scenario ${scenario.name}` : 'New scenario'} title={scenario ? 'Edit scenario' : 'New scenario'}>
            <SlidersHorizontal size={15} />
          </button>
        </span>
      </label>
      <label style={s.pickLabel}>
        <span style={s.pickCaption}>Baseline month</span>
        <select id="billing-period" aria-label="Baseline month" value={period || ''}
                onChange={(e) => { setPeriod(e.target.value ? Number(e.target.value) : null); setPage(0); }} style={s.pick}>
          {periods.map((p, i) => (
            <option key={p.period} value={i === 0 ? '' : p.period}>
              {periodLabel(p.period)}{i === 0 ? ' (latest)' : ''}
            </option>
          ))}
        </select>
      </label>
      <label style={s.pickLabel}>
        <span style={s.pickCaption}>Company</span>
        <select id="billing-company" aria-label="Company" value={company}
                onChange={(e) => narrow(setCompany)(e.target.value)} style={{ ...s.pick, minWidth: '120px' }}>
          <option value="">All companies</option>
          {meta.companies.map((co) => <option key={co} value={co}>{co}</option>)}
        </select>
      </label>
    </div>
  );

  return (
    <div style={s.page}>
      <style>{css}</style>
      <PageHeader
        eyebrow="Finance"
        title="Customer Billing"
        subtitle="What every customer was billed, from the Soft-Pak sale detail export, and what a billing change would do to each of them and to the company. Amounts are pre-tax."
        actions={periods.length ? controls : null}
        alignActions="end"
      />

      {!periods.length ? (
        <div style={s.emptyCard}>
          No billing has been loaded yet. A super admin loads the Soft-Pak export under Data Uploads → Customer Billing Upload.
        </div>
      ) : (
        <>
          {!scenario ? (
            <div style={s.note} role="note">
              <AlertTriangle size={14} />
              <span>
                No scenario is selected, so projected equals billed. Choose a scenario, or{' '}
                <button type="button" style={s.inlineLink} onClick={() => setEditing('new')}>create one</button>, to see what a billing change does.
              </span>
            </div>
          ) : (
            <div style={s.scenarioBar}>
              <span><strong>{scenario.name}</strong>{scenario.description ? ` — ${scenario.description}` : ''}</span>
              <span style={s.scenarioRates}>{ratesText(scenario.rates, meta.categories)}</span>
            </div>
          )}

          {summaryQ.isLoading ? <LoadingSpinner /> : summaryQ.error ? (
            <div style={s.error}>{summaryQ.error.message}</div>
          ) : summaryQ.data?.period ? (
            <>
              <ImpactSummary summary={summaryQ.data} periods={periods} hasScenario={!!scenario}
                             category={category} onCategory={chooseCategory} />
              <div style={s.row2}>
                <IncreaseBands bands={summaryQ.data.bands} selected={band}
                               onSelect={(k) => { setBand(k === band ? null : k); setPage(0); }} />
              </div>
            </>
          ) : null}

          <div className="cb-master-detail">
            <CustomerList
              data={customersQ.data}
              loading={customersQ.isLoading}
              refreshing={customersQ.isPlaceholderData && customersQ.isFetching}
              error={customersQ.error}
              selected={account}
              onSelect={setAccount}
              bandLabel={meta.bands.find((b) => b.key === band)?.label || null}
              onClearBand={() => { setBand(null); setPage(0); }}
              category={category}
              categoryLabel={meta.categories.find((x) => x.key === category)?.label || null}
              onClearCategory={() => chooseCategory(null)}
              search={search}
              onSearch={narrow(setSearch)}
              sort={sort}
              onSort={narrow(setSort)}
              page={page}
              pageSize={PAGE}
              onPage={setPage}
              exportParams={exportParams}
            />
            <CustomerDetail
              account={account}
              data={detailQ.data}
              loading={detailQ.isLoading || (detailQ.isFetching && detailQ.data?.account !== account)}
              error={detailQ.error}
              scenario={scenario}
              categories={meta.categories}
              exportParams={ctx}
            />
          </div>
        </>
      )}

      {editing && (
        <ScenarioEditor
          scenario={editing === 'new' ? null : editing}
          categories={meta.categories}
          onClose={() => setEditing(null)}
          onSaved={(saved) => { setScenarioId(saved.id); setEditing(null); }}
          onDeleted={() => { setScenarioId(''); setEditing(null); }}
        />
      )}
    </div>
  );
}

const PAGE = 50;

// In the categories' own order: the stored rates are jsonb, whose keys come
// back in whatever order Postgres keeps them.
function ratesText(rates, categories) {
  const parts = categories.filter((x) => rates?.[x.key] !== undefined)
    .map((x) => `${x.label} ${rates[x.key] > 0 ? '+' : ''}${rates[x.key]}%`);
  return parts.length ? parts.join(' · ') : 'No rate changes';
}

// The list beside the customer, stacked on a narrow screen. A media query, so
// real CSS: an inline style object cannot express one.
const css = `
.cb-master-detail {
  display: grid; grid-template-columns: minmax(300px, 360px) minmax(0, 1fr);
  gap: 14px; align-items: start; margin-bottom: 14px;
}
@media (max-width: 960px) {
  .cb-master-detail { grid-template-columns: minmax(0, 1fr); }
}
`;

const s = {
  page:        { maxWidth: '1500px', margin: '0 auto', paddingBottom: '8px' },
  controls:    { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' },
  pickLabel:   { display: 'flex', flexDirection: 'column', gap: '3px' },
  pickCaption: { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textFaint },
  pick:        { height: '32px', fontSize: '12.5px', fontFamily: 'inherit', padding: '0 8px', border: `1px solid ${c.border}`, borderRadius: '6px', background: c.surface, color: c.text, minWidth: '150px', cursor: 'pointer' },
  iconBtn:     { ...btn.iconBordered, width: '32px', height: '32px', background: c.surface },
  note:        { display: 'flex', alignItems: 'center', gap: '8px', background: c.warningTint, border: '1px solid #FDE68A', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '12.5px', color: c.warningText },
  inlineLink:  { ...btn.link, fontSize: '12.5px' },
  scenarioBar: { display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', background: c.infoTint, border: '1px solid #BFDBFE', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '12.5px', color: c.textStrong },
  scenarioRates: { color: c.textMuted, fontSize: '12px' },
  row2:        { marginBottom: '14px' },
  emptyCard:   { ...card.sm, padding: '56px 24px', textAlign: 'center', color: c.textMuted, fontSize: '13px' },
  error:       { padding: '16px', color: c.dangerText, fontSize: '12.5px' },
};
